import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/app/store/hooks";

import { useRefreshTokenMutation } from "@/features/auth/api/authApi";
import { logout, tokenRefreshed } from "@/features/auth/authSlice";
import { isAccessTokenExpired, secondsUntilExpiry } from "@/features/auth/lib/accessToken";
import { stashReturnPath } from "@/features/auth/lib/sessionReturnPath";
import { useToast } from "@/shared/ui/Toast";

const WARN_SECONDS = 5 * 60;
const POLL_MS = 15_000;

/** Watches JWT exp: warn 5 minutes out, then stash path and send the user to login. */
export function SessionGuard() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const refreshToken = useAppSelector((s) => s.auth.refreshToken);
  const [refresh] = useRefreshTokenMutation();
  const warnedRef = useRef(false);

  useEffect(() => {
    warnedRef.current = false;
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return undefined;

    const tick = () => {
      const remaining = secondsUntilExpiry(accessToken);
      if (remaining === null) return;
      if (remaining <= 0 || isAccessTokenExpired(accessToken)) {
        stashReturnPath(`${location.pathname}${location.search}`);
        dispatch(logout());
        navigate("/login", { replace: true });
        return;
      }
      if (remaining <= WARN_SECONDS && !warnedRef.current) {
        warnedRef.current = true;
        toast.info("Your session expires in 5 minutes. Stay logged in?", {
          persist: true,
          actionLabel: "Stay logged in",
          onAction: () => {
            if (!refreshToken) return;
            void refresh({ refresh: refreshToken })
              .unwrap()
              .then((result) => {
                dispatch(tokenRefreshed({ access: result.access }));
                toast.success("Session extended.");
              })
              .catch(() => {
                toast.error("Could not extend the session. Please log in again.");
              });
          },
        });
      }
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, [accessToken, dispatch, location.pathname, location.search, navigate, refresh, refreshToken, toast]);

  return null;
}
